import _ from "lodash";
import moment from "moment";
import "moment/locale/es";
import { Metric } from "../../../../cores/analytics/interface/api/get-insights";
import { FacebookPagePost } from "../../../../cores/analytics/interface/api/get-facebook-posts";
import { InstagramPost } from "../../../../cores/analytics/interface/api/get-instagram-feed-posts";
import { RawSummaryData } from "./fetchSummaryAnalytics";
import {
  SummaryAnalyticsSnapshot,
  SummaryMetricBlock,
  SummaryPlatform,
  SummaryPlatformValue,
  SummaryPostRow,
  SummarySeriesPoint,
  SummaryTrendDir,
} from "./summaryAnalyticsTypes";

const toEntities = (metrics: Metric[]): Record<string, Metric> =>
  Object.fromEntries(metrics.map((m) => [m.name, m]));

const pickMetric = (
  entities: Record<string, Metric>,
  names: string[]
): Metric | undefined => {
  for (const name of names) {
    const m = entities[name];
    if (
      m &&
      ((m.values && m.values.length > 0) ||
        typeof m.total?.value === "number")
    ) {
      return m;
    }
  }
  return undefined;
};

const totalMetricValue = (m?: Metric): number => {
  if (!m) return 0;
  if (typeof m.total?.value === "number") return m.total.value;
  if (m.values?.length) {
    return m.values.reduce((acc, v) => acc + (Number(v.value) || 0), 0);
  }
  return 0;
};

const dateKey = (label: string): string | null => {
  if (!label?.trim()) return null;
  const iso = moment(label, "YYYY-MM-DD", true);
  if (iso.isValid()) return iso.format("YYYY-MM-DD");
  const formats = [
    "MM/DD/YY",
    "MM/DD/YYYY",
    "DD/MM/YY",
    "DD/MM/YYYY",
    "MMMM D, YYYY",
    "dddd, MMMM Do YYYY",
    "MMMM Do YYYY",
  ];
  for (const fmt of formats) {
    const m = moment(label, fmt, true);
    if (m.isValid()) return m.format("YYYY-MM-DD");
  }
  const loose = moment(label.replace(/(\d+)(st|nd|rd|th)\b/gi, "$1"));
  return loose.isValid() ? loose.format("YYYY-MM-DD") : null;
};

const formatAxisLabelEs = (key: string): string =>
  moment(key, "YYYY-MM-DD").locale("es").format("D MMM");

const valuesToDayMap = (m?: Metric): Map<string, number> => {
  const map = new Map<string, number>();
  if (!m?.values) return map;
  for (const { label, value } of m.values) {
    const k = dateKey(label);
    if (k) map.set(k, Number(value) || 0);
  }
  return map;
};

const sumMetricInRange = (
  m: Metric | undefined,
  since: string,
  until: string
): number => {
  if (!m?.values?.length) return totalMetricValue(m);
  const start = moment(since).startOf("day");
  const end = moment(until).endOf("day");
  if (!start.isValid() || !end.isValid()) return totalMetricValue(m);
  let sum = 0;
  for (const { label, value } of m.values) {
    const k = dateKey(label);
    if (!k) continue;
    const d = moment(k, "YYYY-MM-DD");
    if (d.isSameOrAfter(start) && d.isSameOrBefore(end)) {
      sum += Number(value) || 0;
    }
  }
  return sum;
};

const sortedMetricPointsInRange = (
  m: Metric | undefined,
  since: string,
  until: string
): { k: string; v: number }[] => {
  if (!m?.values?.length) return [];
  const start = moment(since).startOf("day");
  const end = moment(until).endOf("day");
  const pts = m.values
    .map((v) => {
      const k = dateKey(v.label);
      return k ? { k, v: Number(v.value) || 0 } : null;
    })
    .filter(Boolean) as { k: string; v: number }[];
  pts.sort((a, b) => moment(a.k).valueOf() - moment(b.k).valueOf());
  if (!start.isValid() || !end.isValid()) return pts;
  return pts.filter(({ k }) => {
    const d = moment(k, "YYYY-MM-DD");
    return d.isSameOrAfter(start) && d.isSameOrBefore(end);
  });
};

const lastMetricValueInRange = (
  m: Metric | undefined,
  since: string,
  until: string
): number => {
  const pts = sortedMetricPointsInRange(m, since, until);
  if (pts.length) return pts[pts.length - 1].v;
  return totalMetricValue(m);
};

const trendDir = (current: number, previous: number): SummaryTrendDir => {
  if (current > previous) return "up";
  if (current < previous) return "down";
  return "flat";
};

const buildSeries = (
  since: string,
  until: string,
  platformMaps: Partial<Record<SummaryPlatform, Map<string, number>>>
): SummarySeriesPoint[] => {
  const start = moment(since).startOf("day");
  const end = moment(until).startOf("day");
  if (!start.isValid() || !end.isValid()) return [];

  const series: SummarySeriesPoint[] = [];
  const cursor = start.clone();
  while (cursor.isSameOrBefore(end, "day")) {
    const iso = cursor.format("YYYY-MM-DD");
    const values: Partial<Record<SummaryPlatform, number>> = {};
    for (const [platform, map] of Object.entries(platformMaps)) {
      if (map) values[platform as SummaryPlatform] = map.get(iso) ?? 0;
    }
    series.push({ label: formatAxisLabelEs(iso), iso, values });
    cursor.add(1, "day");
  }
  return series;
};

const buildMetricBlock = (
  currentByPlatform: SummaryPlatformValue[],
  previousByPlatform: Partial<Record<SummaryPlatform, number>>,
  seriesMaps: Partial<Record<SummaryPlatform, Map<string, number>>>,
  since: string,
  until: string
): SummaryMetricBlock => {
  const total = currentByPlatform.reduce((a, p) => a + p.value, 0);
  const prevTotal = currentByPlatform.reduce(
    (a, p) => a + (previousByPlatform[p.platform] ?? 0),
    0
  );
  const platforms = currentByPlatform.map((p) => ({
    ...p,
    direction: trendDir(p.value, previousByPlatform[p.platform] ?? 0),
  }));
  return {
    total,
    direction: trendDir(total, prevTotal),
    platforms,
    series: buildSeries(since, until, seriesMaps),
  };
};

const aggregateFacebookPosts = (posts: FacebookPagePost[]) => {
  const interactions = posts.reduce(
    (a, p) =>
      a +
      (p.reactionCount || 0) +
      (p.commentCount || 0) +
      (p.shareCount || 0) +
      (p.clicks || 0),
    0
  );
  const impressions = posts.reduce((a, p) => a + (p.views || 0), 0);
  return { count: posts.length, interactions, impressions };
};

const postsByDay = (
  posts: { timestamp: number }[],
  valueFn: (count: number) => number = () => 1
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const post of posts) {
    const ts = Number(post.timestamp) || 0;
    if (!ts) continue;
    const key = moment.unix(ts).format("YYYY-MM-DD");
    map.set(key, (map.get(key) ?? 0) + valueFn(1));
  }
  return map;
};

const fbInteractionsByDay = (posts: FacebookPagePost[]): Map<string, number> => {
  const map = new Map<string, number>();
  for (const post of posts) {
    const ts = Number(post.timestamp) || 0;
    if (!ts) continue;
    const key = moment.unix(ts).format("YYYY-MM-DD");
    const val =
      (post.reactionCount || 0) +
      (post.commentCount || 0) +
      (post.shareCount || 0) +
      (post.clicks || 0);
    map.set(key, (map.get(key) ?? 0) + val);
  }
  return map;
};

const fbImpressionsByDay = (posts: FacebookPagePost[]): Map<string, number> => {
  const map = new Map<string, number>();
  for (const post of posts) {
    const ts = Number(post.timestamp) || 0;
    if (!ts) continue;
    const key = moment.unix(ts).format("YYYY-MM-DD");
    map.set(key, (map.get(key) ?? 0) + (post.views || 0));
  }
  return map;
};

const igInteractionsByDay = (entities: Record<string, Metric>): Map<string, number> =>
  valuesToDayMap(pickMetric(entities, ["engagement"]));

const igImpressionsByDay = (entities: Record<string, Metric>): Map<string, number> =>
  valuesToDayMap(pickMetric(entities, ["impressions", "views"]));

const mapFacebookPost = (post: FacebookPagePost): SummaryPostRow => {
  const ts = Number(post.timestamp) || 0;
  const m = ts > 0 ? moment.unix(ts).locale("es") : moment().locale("es");
  const caption = String(post.message || "").trim();
  return {
    id: `fb-${post.id}`,
    platform: "facebook",
    title: caption || "(Sin texto)",
    typeLabel: post.typeLabel || "Publicación",
    dateLabel: m.format("D MMM YYYY"),
    timeLabel: m.format("HH:mm"),
    impressions: post.views || 0,
    permalink: post.permalink,
  };
};

const mapInstagramPost = (post: InstagramPost): SummaryPostRow => {
  const ts = Number(post.timestamp) || 0;
  const m = ts > 0 ? moment.unix(ts).locale("es") : moment().locale("es");
  const mediaType = Number(post.mediaType);
  const typeLabel =
    mediaType === 8 ? "Carrusel" : mediaType === 2 ? "Reel" : "Post";
  const text = post.text?.trim() || "(Sin texto)";
  return {
    id: `ig-${post.id}`,
    platform: "instagram",
    title: text,
    typeLabel,
    dateLabel: m.format("D MMM YYYY"),
    timeLabel: m.format("HH:mm"),
    impressions: Number(post.viewCount) || Number(post.reach) || 0,
    thumbnailUrl: post.thumbnailUrl || post.imageUrls?.[0],
    permalink: post.permalink,
  };
};

const formatPeriodLabel = (since: string, until: string) => {
  const fmt = (iso: string) => {
    const m = moment(iso).locale("es");
    const month = m.format("MMM").replace(/\./g, "").toLowerCase();
    return `${m.date()} ${month}. ${m.year()}`;
  };
  return `${fmt(since)} - ${fmt(until)}`;
};

export function buildSummaryAnalytics(raw: RawSummaryData): SummaryAnalyticsSnapshot {
  const syncedPlatforms: SummaryPlatform[] = [];
  const followerCurrent: Partial<Record<SummaryPlatform, number>> = {};
  const followerPrevious: Partial<Record<SummaryPlatform, number>> = {};
  const followerSeriesMaps: Partial<Record<SummaryPlatform, Map<string, number>>> =
    {};

  const interactionsCurrent: Partial<Record<SummaryPlatform, number>> = {};
  const interactionsPrevious: Partial<Record<SummaryPlatform, number>> = {};
  const interactionsSeriesMaps: Partial<
    Record<SummaryPlatform, Map<string, number>>
  > = {};

  const impressionsCurrent: Partial<Record<SummaryPlatform, number>> = {};
  const impressionsPrevious: Partial<Record<SummaryPlatform, number>> = {};
  const impressionsSeriesMaps: Partial<
    Record<SummaryPlatform, Map<string, number>>
  > = {};

  const publicationsCurrent: Partial<Record<SummaryPlatform, number>> = {};
  const publicationsPrevious: Partial<Record<SummaryPlatform, number>> = {};
  const publicationsSeriesMaps: Partial<
    Record<SummaryPlatform, Map<string, number>>
  > = {};

  const postRows: SummaryPostRow[] = [];

  if (raw.facebook) {
    syncedPlatforms.push("facebook");
    const entities = toEntities(raw.facebook.insights);
    const prevEntities = toEntities(raw.facebook.previousInsights);
    const fans = pickMetric(entities, ["page_fans", "page_follows"]);
    const prevFans = pickMetric(prevEntities, ["page_fans", "page_follows"]);
    followerCurrent.facebook = Math.round(
      lastMetricValueInRange(fans, raw.since, raw.until)
    );
    followerPrevious.facebook = Math.round(
      lastMetricValueInRange(prevFans, raw.previousSince, raw.previousUntil)
    );
    followerSeriesMaps.facebook = valuesToDayMap(fans);

    const fbAgg = aggregateFacebookPosts(raw.facebook.posts);
    const fbPrevAgg = aggregateFacebookPosts(raw.facebook.previousPosts);
    interactionsCurrent.facebook = fbAgg.interactions;
    interactionsPrevious.facebook = fbPrevAgg.interactions;
    interactionsSeriesMaps.facebook = fbInteractionsByDay(raw.facebook.posts);

    const impMetric = pickMetric(entities, ["page_impressions", "page_media_view"]);
    const prevImpMetric = pickMetric(prevEntities, [
      "page_impressions",
      "page_media_view",
    ]);
    impressionsCurrent.facebook = Math.round(
      sumMetricInRange(impMetric, raw.since, raw.until) || fbAgg.impressions
    );
    impressionsPrevious.facebook = Math.round(
      sumMetricInRange(prevImpMetric, raw.previousSince, raw.previousUntil) ||
        fbPrevAgg.impressions
    );
    impressionsSeriesMaps.facebook =
      valuesToDayMap(impMetric).size > 0
        ? valuesToDayMap(impMetric)
        : fbImpressionsByDay(raw.facebook.posts);

    publicationsCurrent.facebook = fbAgg.count;
    publicationsPrevious.facebook = fbPrevAgg.count;
    publicationsSeriesMaps.facebook = postsByDay(raw.facebook.posts);

    postRows.push(...raw.facebook.posts.map(mapFacebookPost));
  }

  if (raw.instagram) {
    syncedPlatforms.push("instagram");
    const entities = toEntities(raw.instagram.insights);
    const prevEntities = toEntities(raw.instagram.previousInsights);
    const followers = pickMetric(entities, ["followers_count", "follower_count"]);
    const prevFollowers = pickMetric(prevEntities, [
      "followers_count",
      "follower_count",
    ]);
    followerCurrent.instagram = Math.round(totalMetricValue(followers));
    followerPrevious.instagram = Math.round(totalMetricValue(prevFollowers));
    followerSeriesMaps.instagram = valuesToDayMap(
      pickMetric(entities, ["follower_count"])
    );

    const engagement = pickMetric(entities, ["engagement"]);
    const prevEngagement = pickMetric(prevEntities, ["engagement"]);
    interactionsCurrent.instagram = Math.round(
      sumMetricInRange(engagement, raw.since, raw.until)
    );
    interactionsPrevious.instagram = Math.round(
      sumMetricInRange(prevEngagement, raw.previousSince, raw.previousUntil)
    );
    interactionsSeriesMaps.instagram = igInteractionsByDay(entities);

    const impressions = pickMetric(entities, ["impressions", "views"]);
    const prevImpressions = pickMetric(prevEntities, ["impressions", "views"]);
    impressionsCurrent.instagram = Math.round(
      sumMetricInRange(impressions, raw.since, raw.until)
    );
    impressionsPrevious.instagram = Math.round(
      sumMetricInRange(prevImpressions, raw.previousSince, raw.previousUntil)
    );
    impressionsSeriesMaps.instagram = igImpressionsByDay(entities);

    publicationsCurrent.instagram = raw.instagram.posts.length;
    publicationsPrevious.instagram = raw.instagram.previousPosts.length;
    publicationsSeriesMaps.instagram = postsByDay(raw.instagram.posts);

    postRows.push(...raw.instagram.posts.map(mapInstagramPost));
  }

  if (raw.tiktok) {
    syncedPlatforms.push("tiktok");
    const { snapshot, previousSnapshot } = raw.tiktok;
    followerCurrent.tiktok = snapshot.growth.followers.value;
    followerPrevious.tiktok = previousSnapshot.growth.followers.value;
    const followerMap = new Map<string, number>();
    for (const day of snapshot.growth.followersSeries) {
      if (day.iso) followerMap.set(day.iso, day.followerTotal ?? 0);
    }
    followerSeriesMaps.tiktok = followerMap;

    interactionsCurrent.tiktok = snapshot.postsPeriod.interactions.total.value;
    interactionsPrevious.tiktok =
      previousSnapshot.postsPeriod.interactions.total.value;
    const interactionsMap = new Map<string, number>();
    for (const day of snapshot.postsPeriod.interactionsSeries) {
      if (day.iso) interactionsMap.set(day.iso, day.interactions ?? 0);
    }
    interactionsSeriesMaps.tiktok = interactionsMap;

    impressionsCurrent.tiktok = snapshot.postsPeriod.views.value;
    impressionsPrevious.tiktok = previousSnapshot.postsPeriod.views.value;
    const viewsMap = new Map<string, number>();
    for (const day of snapshot.postsPeriod.viewsSeries) {
      if (day.iso) viewsMap.set(day.iso, day.views ?? 0);
    }
    impressionsSeriesMaps.tiktok = viewsMap;

    publicationsCurrent.tiktok = snapshot.growth.posts.value;
    publicationsPrevious.tiktok = previousSnapshot.growth.posts.value;
    const postsMap = new Map<string, number>();
    for (const day of snapshot.growth.followersSeries) {
      if (day.iso) postsMap.set(day.iso, day.postsCount ?? 0);
    }
    publicationsSeriesMaps.tiktok = postsMap;

    for (const row of snapshot.postsTable) {
      postRows.push({
        id: `tt-${row.id}`,
        platform: "tiktok",
        title: row.caption,
        typeLabel: row.typeLabel,
        dateLabel: row.dateLabel,
        timeLabel: row.timeLabel,
        impressions: row.views,
      });
    }
  }

  const toPlatformValues = (
    current: Partial<Record<SummaryPlatform, number>>
  ): SummaryPlatformValue[] =>
    syncedPlatforms.map((platform) => ({
      platform,
      value: Math.round(current[platform] ?? 0),
    }));

  const sortedPosts = _.orderBy(postRows, [(p) => p.impressions], ["desc"]);

  return {
    periodLabel: formatPeriodLabel(raw.since, raw.until),
    syncedPlatforms,
    account: {
      followers: buildMetricBlock(
        toPlatformValues(followerCurrent),
        followerPrevious,
        followerSeriesMaps,
        raw.since,
        raw.until
      ),
    },
    posts: {
      interactions: buildMetricBlock(
        toPlatformValues(interactionsCurrent),
        interactionsPrevious,
        interactionsSeriesMaps,
        raw.since,
        raw.until
      ),
      impressions: buildMetricBlock(
        toPlatformValues(impressionsCurrent),
        impressionsPrevious,
        impressionsSeriesMaps,
        raw.since,
        raw.until
      ),
      publications: buildMetricBlock(
        toPlatformValues(publicationsCurrent),
        publicationsPrevious,
        publicationsSeriesMaps,
        raw.since,
        raw.until
      ),
      postList: sortedPosts,
    },
  };
}
